<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="process-flow-title" :field="fields?.title" tag="h2" />
      <JssRichText class="process-flow-desc" :field="fields?.subtitle" tag="p" />
      <div class="process-flow-body" v-if="fields?.list?.length > 0">
        <div class="process-flow-body-line" v-if="!hasNoImages"></div>
        <Swiper
          :modules="[Navigation]"
          :space-between="30"
          :slides-per-view="1"
          :breakpoints="{
            1024: {
              slidesPerView: fields?.list?.length,
            },
          }"
          class="process-flow-body-swiper"
        >
          <SwiperSlide
            v-for="(item, index) in fields?.list"
            :key="index"
            class="process-flow-body-swiper-item"
            :class="{
              'align-left': fields?.layout?.fields?.phrase?.value === 'left',
              'align-center': fields?.layout?.fields?.phrase?.value === 'center',
              'align-right': fields?.layout?.fields?.phrase?.value === 'right',
            }"
          >
            <div class="process-flow-body-swiper-item-icon">
              <AdaptiveImage
                :field="item.fields?.image"
                :isBackground="false"
                :lazy-load="false"
                :enableScrollDetection="false"
                :show="true"
              />
            </div>
            <JssRichText
              class="process-flow-body-swiper-item-time"
              :field="item.fields?.time"
              v-if="item.fields?.time?.value"
            />
            <JssRichText
              class="process-flow-body-swiper-item-title"
              :field="item.fields?.title"
              tag="h3"
            />
            <JssRichText
              class="process-flow-body-swiper-item-subtitle"
              :field="item.fields?.subtitle"
              tag="h4"
            />
            <JssRichText class="process-flow-body-swiper-item-desc" :field="item.fields?.desc" />
            <JssRichText
              class="process-flow-body-swiper-item-tag"
              :field="item.fields?.tag"
              :style="{ backgroundColor: item.fields?.tagBgColor?.value }"
              v-if="item.fields?.tag?.value"
            />
          </SwiperSlide>
        </Swiper>
      </div>
      <div class="process-flow-footer" v-if="fields?.desc?.value">
        <JssRichText class="process-flow-footer-text" :field="fields?.desc" />
        <SiteButton v-bind="fields?.button" />
      </div>
      <div class="process-flow-time" v-if="fields?.time?.value">
        <JssRichText class="process-flow-time-text" :field="fields?.time" />
        <div class="process-flow-time-line">
          <div class="process-flow-time-line-inner"></div>
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import utils from '@/utils';

export default {
  name: 'ProcessFlow',
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'process-flow'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const hasNoImages = computed(() => {
      const list = props.fields?.list || [];
      return list.some((item) => !item?.fields?.image);
    });

    return {
      rootClass,
      rootStyle,
      hasNoImages,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.process-flow {
  position: relative;
  width: 100%;
  padding: 40px 0;

  .e-content-grid__content {
    overflow: hidden;
  }

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
    text-align: center;
  }

  &-body {
    position: relative;
    margin-top: 24px;

    &-line {
      display: none;
    }

    &-swiper {
      width: 100%;
      position: relative;
      z-index: 2;

      &-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        position: relative;
        padding: 20px 10px;
        border-radius: 12px;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.3s ease;

        &.align-left {
          align-items: flex-start;
          text-align: left;

          .process-flow-body-swiper-item-icon {
            justify-content: flex-start;
          }
        }

        &.align-right {
          align-items: flex-end;
          text-align: right;

          .process-flow-body-swiper-item-icon {
            justify-content: flex-end;
          }
        }

        &:hover {
          background: #f8faf8;
          border-color: $blue-night;
          box-shadow: 0 4px 12px rgba(107, 145, 113, 0.15);

          .process-flow-body-swiper-item-title {
            color: $blue-night;
          }
        }

        &-icon {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          transition: transform 0.3s ease;
        }

        &-time {
          font-size: 12px;
          font-weight: 600;
          color: $grey-black;
          margin-bottom: 6px;
          padding: 3px 6px;
          border-radius: 4px;
          background: #eff5e8;
        }

        &-title {
          font-size: 16px;
          font-weight: 700;
          color: $grey-black;
          margin-bottom: 12px;
        }

        &-subtitle {
          font-size: 13px;
          font-weight: 600;
          color: $blue-night;
          margin-bottom: 6px;
        }

        &-desc {
          font-size: 12px;
          color: #787878;
          line-height: 1.4;
          margin-bottom: 8px;
        }

        &-tag {
          font-size: 16px;
          font-weight: 700;
          color: $white;
          width: 100%;
          text-align: center;
          padding: 12px 16px;
          border-radius: 40px;
          display: inline-block;
          margin-top: 24px;
        }
      }
    }
  }

  &-footer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 12px;
    background: $grey-neutral;
    border-radius: 4px;
    padding: 20px 16px;
    gap: 16px;

    &-text {
      font-size: 14px;
      color: $grey-black;
      font-weight: 500;
      text-align: center;
    }
  }

  &-time {
    margin-top: 20px;
    overflow: visible;

    &-text {
      font-size: 16px;
      line-height: 1.2;
      font-weight: 700;
      color: $blue-night;
      text-align: center;
    }

    &-line {
      width: 100%;
      height: 18px;
      margin-top: 15px;
      position: relative;
      overflow: visible;
      display: flex;
      align-items: center;
      justify-content: center;

      &-inner {
        width: calc(100% - 15px);
        height: 6px;
        background: #c7d3c7;
      }

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: 0px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-left: 12px solid #c7d3c7;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-body {
      position: relative;
      margin-top: 40px;

      &-line {
        display: block;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, #d4d4d4, #a8a8a8);
        position: absolute;
        top: 61px;
        left: 0;
        z-index: 1;
      }

      &-swiper {
        &-item {
          padding: 28px 15px;
          align-items: flex-start;
          text-align: left;
          position: relative;

          &.align-center {
            align-items: center;
            text-align: center;

            .process-flow-body-swiper-item-icon {
              justify-content: center;
            }

            &::after {
              left: calc(50% + 31px);
            }

            &::before {
              right: calc(50% + 31px);
            }
          }

          &.align-right {
            align-items: flex-end;
            text-align: right;

            .process-flow-body-swiper-item-icon {
              justify-content: flex-end;
            }
          }

          &-icon {
            width: 62px;
            height: 62px;
            margin-bottom: 24px;
            justify-content: flex-start;
            position: relative;
            z-index: 2;
          }

          &-time {
            font-size: 14px;
            padding: 4px 8px;
          }

          &-title {
            font-size: 18px;
            margin-bottom: 16px;
            line-height: 1.4;
            min-height: 2.8em;
          }

          &-subtitle {
            font-size: 14px;
          }

          &-desc {
            font-size: 12px;
          }
        }
      }
    }

    &-footer {
      flex-direction: row;
      justify-content: space-between;
      margin-top: 16px;
      padding: 10px 12px 10px 24px;

      &-text {
        font-size: 16px;
      }
    }
  }
}
</style>
